import { createSelector, createSlice } from "@reduxjs/toolkit";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory } from "../../common/reducers/utilities";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = compartment => compartment.id;

const compartmentSuperSlice = createSlice({
    name: 'superCompartment',
    initialState: {
        data: {}
    },
    reducers: {
        setForEquipment: (state, action) => {
            ensureArray(action.payload.equipmentids).forEach(equipmentid => delete state.data[equipmentid]);
            const toSet = ensureArray(action.payload.data);
            const byEquipment = groupBy(toSet, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, compartments ]) => {
                state.data[equipmentid] = compartments;
            });
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byEquipment = groupBy(toUpdate, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, compartments ]) => {
                const ids = new Set(compartments.map(c => key(c)));
                state.data[equipmentid] = (state.data[equipmentid]?.filter(c => !ids.has(key(c))) ?? []).concat(compartments);
            })
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byEquipment = groupBy(toRemove, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, compartments ]) => {
                if (state.data[equipmentid]) {
                    const ids = new Set(compartments.map(c => key(c)));
                    state.data[equipmentid] = state.data[equipmentid].filter(c => !ids.has(key(c)));
                    if (state.data[equipmentid].length === 0) {
                        delete state.data[equipmentid];
                    }
                }
            })
        },
        removeForEquipment: (state, action) => {
            ensureArray(action.payload).forEach(id => delete state.data[id]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => compartmentSuperSlice.getInitialState());
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'compartment_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'compartment_one');

const fetchCompartmentsForEquipment = aggregatingFetch(
    equipmentid => network => network.post('/api/v2/superCompartmentByEquipment', [equipmentid], data => compartmentSuperSlice.actions.setForEquipment({ equipmentids: [equipmentid], data}), visualizeFailure('get')),
    equipmentids => network => network.post('/api/v2/superCompartmentByEquipment', equipmentids, data => compartmentSuperSlice.actions.setForEquipment({ equipmentids, data}), visualizeFailure('get')),
)
export const createCompartment = (network, data) => network.post('/api/v1/compartment', data, compartmentSuperSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateCompartment = (network, data) => network.put('/api/v1/compartment', data, compartmentSuperSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeCompartment = (network, data) => network.delete(`/api/v1/compartment/${data.id}`, compartmentSuperSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAllCompartments = state => state.superCompartment.data;
const selectCompartmentByEquipment = equipmentid => createSelector([selectAllCompartments], compartments => {
    return compartments[equipmentid];
})
// #endregion

const listen = {
    compartmentCreated: (compartment, { dispatch, websocket }) => {
        markFetched(websocket, 'compartment', key(compartment));
        dispatch(compartmentSuperSlice.actions.addOrUpdate(compartment))
    },
    compartmentUpdated: (compartment, { dispatch }) => dispatch(compartmentSuperSlice.actions.addOrUpdate(compartment)),
    compartmentRemoved: (compartment, { dispatch }) => dispatch(compartmentSuperSlice.actions.remove(compartment)),
    equipmentRemoved: (equipment, { dispatch }) => dispatch(compartmentSuperSlice.actions.removeForEquipment(equipment))
}

const compartmentsInitByID = initMapFactory(fetchCompartmentsForEquipment, listen);
registerEntityBasedFetch('equipment', compartmentsInitByID);

const cleanup = { root: compartmentSuperSlice.name, all:  state => Object.values(selectAllCompartments(state)).flat(), removeAll: compartmentSuperSlice.actions.remove, key };

export const [useSuperCompartments, realizeSuperCompartments] = dataHookFactory(selectCompartmentByEquipment, compartmentsInitByID, {cleanup});

const reducer = { name: compartmentSuperSlice.name, reducer: compartmentSuperSlice.reducer }
export default reducer;